/** Cliente HTTP de la API: token de acceso en memoria, refresh mediante cookie httpOnly (web) y reintento transparente. */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
    public body?: unknown,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(token: string | null) => void>();

export function setAccessToken(t: string | null) {
  accessToken = t;
  listeners.forEach((l) => l(t));
}
export const getAccessToken = () => accessToken;
export function onTokenChange(fn: (t: string | null) => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Identidad (usuario + empresa) de un token de acceso, sin verificarlo: solo para detectar cambios de cuenta. */
export function tokenIdentity(t: string | null): string | null {
  if (!t) return null;
  try {
    const p = JSON.parse(atob(t.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    return `${p.sub}|${p.cid ?? ''}`;
  } catch {
    return null;
  }
}

// La cookie de sesión es única por navegador: si en otra pestaña se entra con otra cuenta, al renovar el token
// esta pestaña recibiría esa otra cuenta. Se avisa para recargar en vez de seguir con datos de la cuenta anterior.
const identityListeners = new Set<() => void>();
export function onSessionReplaced(fn: () => void) {
  identityListeners.add(fn);
  return () => identityListeners.delete(fn);
}

export async function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json', 'x-requested-with': 'wulftime' }, body: '{}' });
      if (!res.ok) {
        setAccessToken(null);
        return false;
      }
      const json = (await res.json()) as { accessToken: string };
      const before = tokenIdentity(accessToken);
      setAccessToken(json.accessToken);
      if (before && before !== tokenIdentity(json.accessToken)) identityListeners.forEach((fn) => fn());
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  raw?: boolean;
  /** Token alternativo (p. ej. kiosco o acceso de soporte). */
  auth?: 'default' | 'none';
}

export async function request<T = unknown>(url: string, opts: RequestOptions = {}, retry = true): Promise<T> {
  const headers: Record<string, string> = { 'x-requested-with': 'wulftime', ...(opts.headers ?? {}) };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (accessToken && opts.auth !== 'none') headers.authorization = `Bearer ${accessToken}`;
  let res: Response;
  try {
    res = await fetch(url, { method: opts.method ?? (body ? 'POST' : 'GET'), headers, body, credentials: 'include' });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Sin conexión con el servidor. Comprueba tu conexión a internet.');
  }
  if (res.status === 401 && retry && opts.auth !== 'none') {
    const err = await res.clone().json().catch(() => ({}));
    if (['TOKEN_EXPIRED', 'UNAUTHORIZED'].includes(err?.error?.code) && (await refreshSession())) return request<T>(url, opts, false);
  }
  if (opts.raw && res.ok) return res as unknown as T;
  const text = await res.text();
  const json = text ? safeJson(text) : null;
  if (!res.ok) {
    const e = (json as { error?: { code: string; message: string; details?: unknown } } | null)?.error;
    throw new ApiError(res.status, e?.code ?? 'ERROR', e?.message ?? `Error ${res.status}`, e?.details, json);
  }
  return json as T;
}

function safeJson(t: string) {
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}

export const api = {
  get: <T = any>(url: string) => request<T>(url),
  post: <T = any>(url: string, body?: unknown) => request<T>(url, { method: 'POST', body: body ?? {} }),
  patch: <T = any>(url: string, body?: unknown) => request<T>(url, { method: 'PATCH', body: body ?? {} }),
  put: <T = any>(url: string, body?: unknown) => request<T>(url, { method: 'PUT', body: body ?? {} }),
  del: <T = any>(url: string) => request<T>(url, { method: 'DELETE' }),
};

/** Descarga un archivo autenticado y lo guarda con su nombre. */
export async function download(url: string, fallbackName = 'descarga') {
  const res = await request<Response>(url, { raw: true });
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename="?([^";]+)"?/.exec(cd);
  const name = m ? decodeURIComponent(m[1]!) : fallbackName;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  return { name, sha256: res.headers.get('x-content-sha256') };
}

export const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : '';
};
